import { useEffect, useState } from "react"
import useAccess from "@/hooks/useAccess"
import { AccessRole } from "@/types"
import Callout from "components/callout/callout"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { InputMaxLength, StorageWrite, StorageSettings, StoragePlan } from "types"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import ToggleInput from "components/inputs/toggleInput"
import Button from "components/inputs/button"
import useStorages from "hooks/useStorages"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import { ReactComponent as SaveIcon } from "assets/icons/save.svg"
import ProcessingButton from "components/inputs/processingButton"
import Section from "components/layout/section"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { logger } from "utils/secureLogger"
import { getTargetStorageDocId, setTargetStorage } from "@/utils/targetStorage"
import useStorageLimits from "hooks/useStorageLimits"

interface EditStorageFormProps {
    storageId: string
}

const EditStorageForm: React.FC<EditStorageFormProps> = ({ storageId }) => {

    const { t } = useTranslation()
    const { addToast } = useLayout()

    // Form data.
    const [name, setName] = useState("")
    const [description, setDescription] = useState("")
    const [location, setLocation] = useState("")
    const [defaultProductUnit, setDefaultProductUnit] = useState("")
    const [storagePlan, setStoragePlan] = useState<StoragePlan>(StoragePlan.free)
    const [enableTransactions, setEnableTransactions] = useState(false)
    const [enableCustomersAndSuppliers, setEnableCustomersAndSuppliers] = useState(false)
    const [requireActionsLinkedToTransactions, setRequireActionsLinkedToTransactions] = useState(false)
    const [enableStockAdjustment, setEnableStockAdjustment] = useState(false)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);
    
    const {
        storage,
        updateStorageAsync,
        isUpdating
    } = useStorages({ storageId })
    
    const { canUseCustomers, canUseSuppliers, canUseTransactions } = useStorageLimits({ storagePlan })

    // Set data from storage.
    useEffect(() => {
        if (storage) {
            setName(storage.name);
            setDescription(storage.description);
            setLocation(storage.location);
            setDefaultProductUnit(storage.settings?.defaultProductUnit || '');
            setStoragePlan(storage.settings?.storagePlan || StoragePlan.free);
            setEnableTransactions(storage.settings?.enableTransactions === true);
            setEnableCustomersAndSuppliers(storage.settings?.enableCustomersAndSuppliers === true);
            setRequireActionsLinkedToTransactions(storage.settings?.requireActionsLinkedToTransactions === true);
            setEnableStockAdjustment(storage.settings?.enableStockAdjustment === true);
        }
    }, [storage])

    // Validate the form.
    const validate = () => {
        let newErrors = [] as string[]
        if (name === '') {
            newErrors = [...newErrors, 'Name is required']
        }
        if (name.length < 3) {
            newErrors = [...newErrors, 'Name must be at least 3 characters']
        }
        setErrors(newErrors)
        return newErrors.length === 0
    }

    // Handle form submit.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (storageId === undefined) return
        if (!validate()) return
        setErrors([])
        
        try {
            const storageData: StorageWrite = {
                id: storageId,
                _version: 1,
                name,
                description,
                location,
                settings: {
                    storagePlan,
                    defaultProductUnit,
                    enableTransactions: enableTransactions === true && canUseTransactions,
                    enableCustomersAndSuppliers: enableCustomersAndSuppliers === true && canUseCustomers && canUseSuppliers,
                    requireActionsLinkedToTransactions: requireActionsLinkedToTransactions === true,
                    enableStockAdjustment: enableStockAdjustment === true
                } as StorageSettings
            }
            await updateStorageAsync(storageData)
            if (getTargetStorageDocId() === storageId) setTargetStorage(storageId, name)
            logger.info('Storage update successful', { storageId }, 'UpdateStorage')
            addToast({ text: t('toastStorageUpdated'), colorScheme: 'green' })
        } catch (error: unknown) {
            logger.error('Failed to update storage', error);
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error));
            setErrors([errorMessage]);
            addToast({ text: t('toastStorageError'), colorScheme: 'red' });
        }
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle description change.
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value)
    }

    // Handle location change.
    const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocation(e.target.value)
    }

    // Handle default product unit change.
    const handleDefaultProductUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDefaultProductUnit(e.target.value)
    }

    // Plan card click
    const handlePlanClick = (plan: StoragePlan) => {
        setStoragePlan(plan)
        if (plan === StoragePlan.free) {
            setEnableTransactions(false)
            setEnableCustomersAndSuppliers(false)
            setRequireActionsLinkedToTransactions(false)
        } else {
            setEnableTransactions(true)
            setEnableCustomersAndSuppliers(true)
            setRequireActionsLinkedToTransactions(true)
        }
    }

    // Handle enable transactions change.
    const handleEnableTransactionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEnableTransactions(e.target.checked);
        if (e.target.checked) {
            setRequireActionsLinkedToTransactions(true);
        } else {
            setEnableCustomersAndSuppliers(false);
            setRequireActionsLinkedToTransactions(false);
        }
    };

    // Handle enable customers and suppliers change.
    const handleEnableCustomersAndSuppliersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEnableCustomersAndSuppliers(e.target.checked);
        if (e.target.checked) {
            setEnableTransactions(true);
        }
    };

    const handleRequireActionsLinkedToTransactionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRequireActionsLinkedToTransactions(e.target.checked);
        if (e.target.checked) {
            setEnableTransactions(true);
        }
    };

    const handleEnableStockAdjustmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEnableStockAdjustment(e.target.checked);
    };

    // Catch missing ID or failed loading storage.
    if (storageId === undefined) return <Callout type={'error'}>{t('storageIdMissing')}</Callout>

    // Only OWNER can use this form (defensive)
    const { limitToRoles } = useAccess({ storageId })
    const isOwner = limitToRoles([AccessRole.OWNER])
    if (!isOwner) {
        return <Callout type="error">{t('calloutRestrictedContent')}</Callout>
    }

    // Print the form.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isUpdating}
        >
            {/* Section 1: Basic Information */}
            <Section>
                <TextInput label={t('name')} value={name} onChange={handleNameChange} required maxLength={InputMaxLength.short} />
                <TextInput label={t('location')} value={location} onChange={handleLocationChange} maxLength={InputMaxLength.medium} />
                <TextAreaInput label={t('description')} value={description} onChange={handleDescriptionChange} maxLength={InputMaxLength.long} />
                <TextInput label={t('defaultProductUnit')} value={defaultProductUnit} onChange={handleDefaultProductUnitChange} maxLength={InputMaxLength.short} />
            </Section>

            {/* Section 2: Plan Selection */}
            <Section design="card-grid-3">
                <Section
                    highlight={storagePlan === StoragePlan.free}
                    highlightable={true}
                    onClick={() => handlePlanClick(StoragePlan.free)}
                    design="card"
                >
                    <div>
                        <h2 className="flex items-center gap-2">
                            {t('storagePlanFree')}
                        </h2>
                        <p className="text-sm text-gray-600 mt-2">{t('storagePlanFreeDescription')}</p>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm">
                        <li>✓ {t('storagePlanFreeProducts')}</li>
                        <li>✓ {t('storagePlanFreeCategories')}</li>
                        <li>✓ 5 custom inputs per category (text, number only)</li>
                    </ul>
                </Section>

                <Section
                    highlight={storagePlan === StoragePlan.pro}
                    highlightable={true}
                    onClick={() => handlePlanClick(StoragePlan.pro)}
                    design="card"
                >
                    <div>
                        <h2 className="flex items-center gap-2">
                            {t('storagePlanPro')}
                        </h2>
                        <p className="text-sm text-gray-600 mt-2">{t('storagePlanProDescription')}</p>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm">
                        <li>✓ {t('storagePlanProProducts')}</li>
                        <li>✓ {t('storagePlanProCategories')}</li>
                        <li>✓ {t('storagePlanProTransactions')}</li>
                        <li>✓ {t('storagePlanProSharedAccess')}</li>
                        <li>✓ {t('storagePlanProCustomers')}</li>
                        <li>✓ {t('storagePlanProSuppliers')}</li>
                        <li>✓ 15 custom inputs per category (all types)</li>
                    </ul>
                </Section>

                <Section
                    highlight={storagePlan === StoragePlan.premium}
                    highlightable={true}
                    onClick={() => handlePlanClick(StoragePlan.premium)}
                    design="card"
                >
                    <div>
                        <h2 className="flex items-center gap-2">
                            {t('storagePlanPremium')}
                        </h2>
                        <p className="text-sm text-gray-600 mt-2">{t('storagePlanPremiumDescription')}</p>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm">
                        <li>✓ {t('storagePlanPremiumProducts')}</li>
                        <li>✓ {t('storagePlanPremiumCategories')}</li>
                        <li>✓ {t('storagePlanPremiumTransactions')}</li>
                        <li>✓ {t('storagePlanPremiumSharedAccess')}</li>
                        <li>✓ {t('storagePlanPremiumCustomers')}</li>
                        <li>✓ {t('storagePlanPremiumSuppliers')}</li>
                        <li>✓ 40 custom inputs per category (all types)</li>
                    </ul>
                </Section>
            </Section>

            {/* Section 3: Feature Settings */}
            <Section>
                <ToggleInput 
                    label={t('labelEnableStockAdjustment')} 
                    helperText={t('helperTextEnableStockAdjustment')} 
                    checked={enableStockAdjustment} 
                    onChange={handleEnableStockAdjustmentChange} 
                />
                {canUseTransactions && (
                    <ToggleInput 
                        label={t('labelEnableTransactions')} 
                        checked={enableTransactions} 
                        onChange={handleEnableTransactionsChange} 
                    />
                )}
                {canUseTransactions && (
                    <ToggleInput 
                        label={t('labelRequireActionsLinkedToTransactions')} 
                        helperText={t('helperTextRequireActionsLinkedToTransactions')} 
                        checked={requireActionsLinkedToTransactions} 
                        onChange={handleRequireActionsLinkedToTransactionsChange}
                        disabled={!enableTransactions}
                    />
                )}
                {(canUseCustomers && canUseSuppliers) && (
                    <ToggleInput 
                        label={t('labelEnableCustomersAndSuppliers')} 
                        helperText={t('helperTextEnableCustomersAndSuppliers')} 
                        checked={enableCustomersAndSuppliers} 
                        onChange={handleEnableCustomersAndSuppliersChange} 
                    />
                )}
            </Section>

            <div className={`input-holder`}>
                {isUpdating ? (
                    <ProcessingButton design="primary" />
                ) : (
                    <Button wideOnMobile type="submit" design="primary"><SaveIcon /><span>{t('saveChanges')}</span></Button>
                )}
            </div>
            {errors.length > 0 && <FormErrors errors={errors} />}
        </Form>
    )
}

export default EditStorageForm


